// pages/EmailLogs.jsx — Super-admin only. Read-only audit trail of every
// email dispatched through utils/emailSender.js (sendEmail + sendEmailText).
import { useEffect, useMemo, useState, useCallback } from "react";
import TopBar from "../components/TopBar";
import Toast from "../components/Toast";
import { apiFetch } from "../utils/api";
import { todayIST as today, daysAgoIST as daysAgo } from "../utils/dateUtils";

// ── CDN loaders (mirrors Reports.jsx pattern) ────────────────────────────────
const loadExcelJS = () => new Promise((resolve, reject) => {
  if (window.ExcelJS) { resolve(window.ExcelJS); return; }
  const s = document.createElement("script");
  s.src = "https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js";
  s.onload = () => resolve(window.ExcelJS);
  s.onerror = reject;
  document.head.appendChild(s);
});

const loadJsPDF = () => new Promise((resolve, reject) => {
  if (window.jspdf && window.jspdf.jsPDF) { resolve(window.jspdf.jsPDF); return; }
  const s1 = document.createElement("script");
  s1.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
  s1.onload = () => {
    const s2 = document.createElement("script");
    s2.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js";
    s2.onload = () => resolve(window.jspdf.jsPDF);
    s2.onerror = reject;
    document.head.appendChild(s2);
  };
  s1.onerror = reject;
  document.head.appendChild(s1);
});

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

// Friendly labels for the raw template_key column (matches EmailModule TYPE_META
// vocabulary where possible).
const TPL_LABEL = {
  review_request: "Review Request",
  request_review: "Request Review",
  reminder: "Reminder",
  reminder_1: "Reminder 1",
  reminder_2: "Reminder 2",
  reminder_3: "Reminder 3",
  reactivation: "Review Reactivated",
  confirmation: "Submission Confirmed",
  admin_notify: "Admin Notification",
  reminder_hit_admin: "Reminder-Hit Admin Alert",
  pdf_copy: "PDF Copy",
  welcome_user: "Welcome — New User",
  stakeholder_otp: "Stakeholder OTP",
  admin_login_otp: "Admin Login OTP",
  forgot_password_otp: "Forgot Password OTP",
  // Legacy keys — kept for older rows logged before the per-type split landed.
  db_template: "DB Template (Reminder)",
  db_template_rich: "DB Template (Request)",
};

const TPL_COLOR = {
  review_request: "#10B981",
  request_review: "#10B981",
  reminder: "#F59E0B",
  reminder_1: "#F59E0B",
  reminder_2: "#EA580C",
  reminder_3: "#DC2626",
  reactivation: "#2563EB",
  confirmation: "#0891B2",
  admin_notify: "#DC2626",
  reminder_hit_admin: "#F59E0B",
  pdf_copy: "#7C3AED",
  welcome_user: "#059669",
  stakeholder_otp: "#0D9488",
  admin_login_otp: "#0D1B2A",
  forgot_password_otp: "#7C3AED",
  db_template: "#F59E0B",
  db_template_rich: "#10B981",
};

// Per-status palette for the audit-trail pill. Mirrors the lifecycle stored
// in email_dispatch_logs.status — see controllers/emailWebhookController.js
// for how SendGrid Event Webhook moves rows through these states.
const STATUS_META = {
  queued:      { label: "Queued",     bg: "#E0E7FF", fg: "#3730A3" },
  delivered:   { label: "Delivered",  bg: "#DCFCE7", fg: "#15803D" },
  bounced:     { label: "Bounced",    bg: "#FEE2E2", fg: "#B91C1C" },
  dropped:     { label: "Dropped",    bg: "#FEE2E2", fg: "#B91C1C" },
  deferred:    { label: "Deferred",   bg: "#FEF3C7", fg: "#92400E" },
  blocked:     { label: "Blocked",    bg: "#FEE2E2", fg: "#B91C1C" },
  spam_report: { label: "Spam",       bg: "#FEE2E2", fg: "#B91C1C" },
  failed:      { label: "Failed",     bg: "#FEE2E2", fg: "#B91C1C" },
  // Legacy: rows written before the lifecycle migration. Treated as "Delivered"
  // by the backfill but old strings may linger in any unrun env — render as a
  // neutral pill instead of crashing.
  sent:        { label: "Sent",       bg: "#DCFCE7", fg: "#15803D" },
};

const fmtDateTime = iso => {
  if (!iso) return "—";
  const d = new Date(iso);
  // Always render in IST — the backend stores IST per config/db.js timezone,
  // so display must pin to "Asia/Kolkata" regardless of the user's browser tz
  // (otherwise a sub-admin opening this page in a different region would see
  // the wrong wall-clock time).
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });
};

// 30-day default range — covers a single review cycle. `today`/`daysAgo`
// are aliased imports of the IST-pinned helpers from dateUtils (see top of file).

const EmailLogs = ({ topBarProps }) => {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [templateKeys, setTemplateKeys] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [toastType, setToastType] = useState("");

  // Filters
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [template, setTemplate] = useState("");
  const [recipient, setRecipient] = useState("");
  const [status, setStatus] = useState("");

  // Row detail drawer
  const [detail, setDetail] = useState(null);

  const showToast = (msg, type = "") => {
    setToast(msg); setToastType(type);
    setTimeout(() => { setToast(""); setToastType(""); }, 2500);
  };

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    if (template) p.set("template", template);
    if (recipient.trim()) p.set("recipient", recipient.trim());
    if (status) p.set("status", status);
    return p.toString();
  }, [from, to, template, recipient, status]);

  const fetchLogs = useCallback(() => {
    setLoading(true);
    apiFetch(`/api/email-logs?${buildQuery()}`)
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data) {
          setRows(d.data.rows || []);
          setTotal(d.data.total || 0);
          setTemplateKeys(d.data.template_keys || []);
        }
      })
      .catch(() => showToast("Failed to load email logs", "error"))
      .finally(() => setLoading(false));
  }, [buildQuery]);

  const fetchStats = useCallback(() => {
    apiFetch("/api/email-logs/stats")
      .then(r => r.json())
      .then(d => { if (d.success && d.data) setStats(d.data); })
      .catch(() => { });
  }, []);

  useEffect(() => { fetchLogs(); fetchStats(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onApplyFilters = () => fetchLogs();
  const onClearFilters = () => {
    setFrom(daysAgo(30)); setTo(today());
    setTemplate(""); setRecipient(""); setStatus("");
    // Re-fetch on next tick so state has flushed
    setTimeout(fetchLogs, 0);
  };

  // ── Export: Excel ──────────────────────────────────────────────────────────
  const exportExcel = async () => {
    if (!rows.length) { showToast("No rows to export"); return; }
    try {
      const ExcelJS = await loadExcelJS();
      const wb = new ExcelJS.Workbook();
      wb.creator = "Dolluz Corp EPR Portal";
      const ws = wb.addWorksheet("Email Logs");

      const headers = ["Sent At", "Template", "To", "CC", "Subject", "Status", "HTTP", "Error", "Attachments"];
      const row1 = ws.getRow(1);
      row1.height = 26;
      headers.forEach((h, i) => {
        const cell = row1.getCell(i + 1);
        cell.value = h;
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8520A" } };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 9 };
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.border = { top: { style: "thin", color: { argb: "FFCB4A00" } }, bottom: { style: "thin", color: { argb: "FFCB4A00" } }, left: { style: "thin", color: { argb: "FFCB4A00" } }, right: { style: "thin", color: { argb: "FFCB4A00" } } };
      });

      rows.forEach((r, idx) => {
        const row = ws.getRow(idx + 2);
        row.height = 18;
        const values = [
          fmtDateTime(r.created_at),
          TPL_LABEL[r.template_key] || r.template_key,
          r.to_email || "",
          r.cc_email || "",
          r.subject || "",
          r.status,
          r.http_status || "",
          r.error_message || "",
          r.attachment_count || 0,
        ];
        const bg = idx % 2 === 0 ? "FFFFFFFF" : "FFF8FAFC";
        values.forEach((val, i) => {
          const cell = row.getCell(i + 1);
          cell.value = val != null ? val : "";
          cell.font = { size: 9, color: { argb: r.status === "failed" ? "FFB91C1C" : "FF0D1B2A" } };
          cell.alignment = { vertical: "middle" };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
          cell.border = { top: { style: "thin", color: { argb: "FFE5E7EB" } }, bottom: { style: "thin", color: { argb: "FFE5E7EB" } }, left: { style: "thin", color: { argb: "FFE5E7EB" } }, right: { style: "thin", color: { argb: "FFE5E7EB" } } };
        });
      });

      ws.columns = [
        { width: 20 }, { width: 22 }, { width: 30 }, { width: 30 },
        { width: 50 }, { width: 10 }, { width: 8 }, { width: 40 }, { width: 12 },
      ];

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      triggerDownload(blob, `EmailLogs_${today()}.xlsx`);
      showToast("Excel exported", "success");
    } catch (e) {
      showToast("Excel export failed: " + e.message, "error");
    }
  };

  // ── Export: PDF ────────────────────────────────────────────────────────────
  const exportPdf = async () => {
    if (!rows.length) { showToast("No rows to export"); return; }
    try {
      const jsPDF = await loadJsPDF();
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageW = 297;

      // Header
      doc.setFillColor(232, 82, 10);
      doc.rect(0, 0, pageW, 18, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12); doc.setFont("helvetica", "bold");
      doc.text("Dolluz Corp EPR  |  Email Dispatch Logs", 10, 11);
      doc.setFontSize(8); doc.setFont("helvetica", "normal");
      const filterLine = [
        from && `From: ${from}`, to && `To: ${to}`,
        template && `Template: ${TPL_LABEL[template] || template}`,
        recipient && `Recipient: ${recipient}`,
        status && `Status: ${status}`,
      ].filter(Boolean).join("  |  ") || "All entries";
      doc.text(filterLine + "  |  Generated: " + new Date().toLocaleString(), 10, 16);

      const body = rows.map(r => ([
        fmtDateTime(r.created_at),
        TPL_LABEL[r.template_key] || r.template_key,
        (r.to_email || "").slice(0, 50),
        (r.subject || "").slice(0, 60),
        r.status,
        r.http_status || "",
        (r.error_message || "").slice(0, 60),
      ]));

      doc.autoTable({
        head: [["Sent At", "Template", "To", "Subject", "Status", "HTTP", "Error"]],
        body,
        startY: 22,
        styles: { fontSize: 7.5, cellPadding: 1.5, overflow: "linebreak" },
        headStyles: { fillColor: [13, 27, 42], textColor: 255, fontStyle: "bold", fontSize: 8 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 30 }, 1: { cellWidth: 32 }, 2: { cellWidth: 50 },
          3: { cellWidth: 75 }, 4: { cellWidth: 16 }, 5: { cellWidth: 14 },
          6: { cellWidth: 60 },
        },
        didParseCell: data => {
          if (data.section === "body" && data.column.index === 4 && data.cell.raw === "failed") {
            data.cell.styles.textColor = [185, 28, 28];
            data.cell.styles.fontStyle = "bold";
          }
        },
      });

      // Footer
      const count = doc.getNumberOfPages();
      for (let i = 1; i <= count; i++) {
        doc.setPage(i);
        doc.setTextColor(148, 163, 184); doc.setFontSize(7); doc.setFont("helvetica", "normal");
        doc.text("Page " + i + " of " + count + "  |  Dolluz Corp EPR Portal", pageW / 2, 207, { align: "center" });
      }

      doc.save(`EmailLogs_${today()}.pdf`);
      showToast("PDF exported", "success");
    } catch (e) {
      showToast("PDF export failed: " + e.message, "error");
    }
  };

  // ── Stat cards ─────────────────────────────────────────────────────────────
  const StatCard = ({ label, total, failed, color }) => {
    const failPct = total > 0 ? ((failed / total) * 100).toFixed(1) : "0.0";
    return (
      <div style={{ flex: 1, background: "#fff", borderRadius: 12, padding: "16px 18px", border: "1px solid #E2E8F0" }}>
        <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>{label}</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 26, fontWeight: 800, color: "#0D1B2A" }}>{total}</div>
          <div style={{ fontSize: 11, color: failed > 0 ? "#DC2626" : "#94A3B8" }}>
            {failed > 0 ? `${failed} failed (${failPct}%)` : "all sent"}
          </div>
        </div>
        <div style={{ height: 3, background: color, marginTop: 10, borderRadius: 2, opacity: 0.85 }} />
      </div>
    );
  };

  const inputStyle = {
    padding: "8px 10px", borderRadius: 8, border: "1.5px solid #E2E8F0",
    fontSize: 12, fontFamily: "'DM Sans',sans-serif", outline: "none",
    background: "#fff", color: "#0D1B2A", minWidth: 140,
  };

  return (
    <>
      <TopBar title="Email Logs" subtitle="Every email dispatched by the EPR Portal — sends, failures, and full audit trail" {...(topBarProps || {})} />

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {/* ── Stat cards ── */}
        {stats && (
          <div style={{ display: "flex", gap: 14 }}>
            <StatCard label="Today" total={stats.today.total} failed={stats.today.failed} color="#10B981" />
            <StatCard label="Last 7 Days" total={stats.week.total} failed={stats.week.failed} color="#3B82F6" />
            <StatCard label="Last 30 Days" total={stats.month.total} failed={stats.month.failed} color="#8B5CF6" />
            <StatCard label="All Time" total={stats.all.total} failed={stats.all.failed} color="#E8520A" />
          </div>
        )}

        {/* ── Filter row ── */}
        <div style={{ background: "#fff", borderRadius: 12, padding: "14px 18px", border: "1px solid #E2E8F0", display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12 }}>
          <div>
            <div style={{ fontSize: 10, color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>From</div>
            <input type="date" style={inputStyle} value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>To</div>
            <input type="date" style={inputStyle} value={to} onChange={e => setTo(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>Template</div>
            <select style={{ ...inputStyle, cursor: "pointer", minWidth: 180 }} value={template} onChange={e => setTemplate(e.target.value)}>
              <option value="">All templates</option>
              {templateKeys.map(k => (
                <option key={k} value={k}>{TPL_LABEL[k] || k}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>Recipient</div>
            <input type="text" placeholder="email contains…" style={{ ...inputStyle, minWidth: 200 }}
              value={recipient} onChange={e => setRecipient(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") onApplyFilters(); }} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>Status</div>
            <select style={{ ...inputStyle, cursor: "pointer" }} value={status} onChange={e => setStatus(e.target.value)}>
              <option value="">Any</option>
              <option value="queued">Queued</option>
              <option value="delivered">Delivered</option>
              <option value="bounced">Bounced</option>
              <option value="dropped">Dropped</option>
              <option value="deferred">Deferred</option>
              <option value="blocked">Blocked</option>
              <option value="spam_report">Spam</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            <button onClick={onClearFilters}
              style={{ padding: "8px 14px", borderRadius: 8, border: "1.5px solid #E2E8F0", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              Clear
            </button>
            <button onClick={onApplyFilters}
              style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#0D1B2A", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              Apply Filters
            </button>
            <button onClick={exportExcel}
              style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: "#10B981", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              ⬇ Excel
            </button>
            <button onClick={exportPdf}
              style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: "#E8520A", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              ⬇ PDF
            </button>
          </div>
        </div>

        {/* ── Result count ── */}
        <div style={{ fontSize: 12, color: "#64748B" }}>
          {loading ? "Loading…" : `Showing ${rows.length} of ${total} ${total === 1 ? "log" : "logs"}`}
          {total > rows.length && <span style={{ color: "#F59E0B", marginLeft: 6 }}>(narrow the date range to see older entries)</span>}
        </div>

        {/* ── Table ── */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#0D1B2A", color: "#fff" }}>
                  <th style={{ padding: "11px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase" }}>Sent At</th>
                  <th style={{ padding: "11px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase" }}>Template</th>
                  <th style={{ padding: "11px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase" }}>To</th>
                  <th style={{ padding: "11px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase" }}>Subject</th>
                  <th style={{ padding: "11px 14px", textAlign: "center", fontWeight: 700, fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase" }}>Status</th>
                  <th style={{ padding: "11px 14px", textAlign: "center", fontWeight: 700, fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase" }}>HTTP</th>
                </tr>
              </thead>
              <tbody>
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: "32px 14px", textAlign: "center", color: "#94A3B8" }}>
                    No logs match the current filters.
                  </td></tr>
                )}
                {rows.map((r, i) => (
                  <tr key={r.id} onClick={() => setDetail(r)}
                    style={{ background: i % 2 === 0 ? "#fff" : "#F8FAFC", cursor: "pointer", borderTop: "1px solid #F1F5F9" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#FFF5F0"}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#F8FAFC"}>
                    <td style={{ padding: "10px 14px", color: "#475569", whiteSpace: "nowrap" }}>{fmtDateTime(r.created_at)}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{
                        background: (TPL_COLOR[r.template_key] || "#64748B") + "18",
                        color: TPL_COLOR[r.template_key] || "#64748B",
                        padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
                      }}>
                        {TPL_LABEL[r.template_key] || r.template_key}
                      </span>
                    </td>
                    <td style={{ padding: "10px 14px", color: "#0D1B2A", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.to_email}</td>
                    <td style={{ padding: "10px 14px", color: "#475569", maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.subject}</td>
                    <td style={{ padding: "10px 14px", textAlign: "center" }}>
                      {(() => {
                        const meta = STATUS_META[r.status] || { label: r.status, bg: "#F1F5F9", fg: "#475569" };
                        return (
                          <span style={{
                            background: meta.bg, color: meta.fg,
                            padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                          }} title={r.last_event ? `Last SG event: ${r.last_event}` : r.status}>
                            {meta.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "center", color: r.status === "failed" ? "#B91C1C" : "#94A3B8", fontFamily: "'Courier New',monospace", fontSize: 12 }}>
                      {r.http_status || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Detail drawer ── */}
      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal" style={{ width: "min(640px, 96vw)", maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ background: "linear-gradient(135deg,#0D1B2A,#1A3352)", padding: "22px 28px", borderRadius: "14px 14px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 16, color: "#fff" }}>Email Log #{detail.id}</div>
                <div style={{ fontSize: 12, color: "#4A6FA5", marginTop: 2 }}>{fmtDateTime(detail.created_at)}</div>
              </div>
              <button onClick={() => setDetail(null)} style={{
                background: "rgba(255,255,255,.1)", border: "none", color: "#fff",
                width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 18,
              }}>&#215;</button>
            </div>
            <div style={{ padding: "20px 28px", display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                ["Template", TPL_LABEL[detail.template_key] || detail.template_key],
                ["Status", (STATUS_META[detail.status] || { label: detail.status }).label],
                ["HTTP", detail.http_status || "—"],
                ["SG Message ID", detail.sg_message_id || "—"],
                ["Last SG Event", detail.last_event ? `${detail.last_event}${detail.last_event_at ? " · " + fmtDateTime(detail.last_event_at) : ""}` : "—"],
                ["Delivered At", detail.delivered_at ? fmtDateTime(detail.delivered_at) : "—"],
                ["To", detail.to_email],
                ["CC", detail.cc_email || "—"],
                ["BCC", detail.bcc_email || "—"],
                ["Subject", detail.subject],
                ["Preview Data", detail.preview || "—"],
                ["Attachments", detail.attachment_count || 0],
                ["Error", detail.error_message || "—"],
              ].map(([lbl, val]) => (
                <div key={lbl} style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 14, alignItems: "start", paddingBottom: 8, borderBottom: "1px solid #F1F5F9" }}>
                  <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{lbl}</div>
                  <div style={{ fontSize: 13, color: lbl === "Error" && val !== "—" ? "#B91C1C" : "#0D1B2A", wordBreak: "break-word" }}>{val}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast} type={toastType} />}
    </>
  );
};

export default EmailLogs;
